/**
 * Customer-side reverse-proxy snippets for article path mode (AC-036) and the robots.txt line (AC-037).
 * Pure: the UI shows and copies exactly these strings. The text is the owner-approved mock
 * (.design/articles-path-mode/mockups/path-provider-snippets.html) filled with the customer's own host and prefix.
 * `publicBaseUrl` must already be validated (utils/publicBaseUrl.ts): https, no trailing slash, no query.
 */
export type ProxyProvider = 'cloudflare' | 'vercel' | 'netlify' | 'nginx' | 'framer';

/** Tab order of the approved mock. */
export const PROXY_PROVIDERS: readonly ProxyProvider[] = ['cloudflare', 'vercel', 'netlify', 'nginx', 'framer'];

export const PROXY_PROVIDER_LABELS: Record<ProxyProvider, string> = {
  cloudflare: 'Cloudflare Worker',
  vercel: 'Vercel',
  netlify: 'Netlify',
  nginx: 'nginx',
  framer: 'Framer',
};

export interface ProxySnippetInput {
  /** The Segmently-served article domain, e.g. articles.client.com. */
  articleHost: string;
  /** The customer's public base, e.g. https://www.client.com/blog. */
  publicBaseUrl: string;
}

/** Owner-approved copy (DESIGN_BRIEF C-13), verbatim. */
const FRAMER_COPY =
  'Framer cannot forward a path on its own. Put Cloudflare in front of your Framer site — Framer allows this on the Scale and Enterprise plans — then add the rule from the Cloudflare Worker tab.';

const prefixOf = (publicBaseUrl: string): string => new URL(publicBaseUrl).pathname.replace(/\/$/, '');

function cloudflareWorker(articleHost: string, prefix: string): string {
  return [
    'export default {',
    '  async fetch(request) {',
    '    const url = new URL(request.url);',
    `    const prefix = '${prefix}';`,
    "    if (url.pathname !== prefix && !url.pathname.startsWith(prefix + '/')) {",
    '      return fetch(request);',
    '    }',
    `    url.hostname = '${articleHost}';`,
    "    url.pathname = url.pathname.slice(prefix.length) || '/';",
    '    return fetch(new Request(url, request));',
    '  },',
    '};',
  ].join('\n');
}

function vercelRewrites(origin: string, prefix: string): string {
  return [
    '{',
    '  "rewrites": [',
    `    { "source": "${prefix || '/'}", "destination": "${origin}/" },`,
    `    { "source": "${prefix}/:path*", "destination": "${origin}/:path*" }`,
    '  ]',
    '}',
  ].join('\n');
}

/** `_redirects` rows with the approved aligned columns (source, target, status). */
function netlifyRedirects(origin: string, prefix: string): string {
  const rows: Array<[string, string]> = [
    ...(prefix ? [[prefix, `${origin}/`] as [string, string]] : []),
    [`${prefix}/*`, `${origin}/:splat`],
  ];
  const sourceWidth = Math.max(...rows.map(([source]) => source.length)) + 2;
  const targetWidth = Math.max(...rows.map(([, target]) => target.length)) + 3;
  return rows.map(([source, target]) => `${source.padEnd(sourceWidth)}${target.padEnd(targetWidth)}200`).join('\n');
}

function nginxLocations(origin: string, articleHost: string, prefix: string): string {
  const block = (location: string) => [
    `location ${location} {`,
    `    proxy_pass ${origin}/;`,
    `    proxy_set_header Host ${articleHost};`,
    '    proxy_ssl_server_name on;',
    '}',
  ];
  return (prefix ? [...block(`= ${prefix}`), ...block(`${prefix}/`)] : block('/')).join('\n');
}

export function buildProxySnippet(provider: ProxyProvider, { articleHost, publicBaseUrl }: ProxySnippetInput): string {
  const prefix = prefixOf(publicBaseUrl);
  const origin = `https://${articleHost}`;
  switch (provider) {
    case 'cloudflare':
      return cloudflareWorker(articleHost, prefix);
    case 'vercel':
      return vercelRewrites(origin, prefix);
    case 'netlify':
      return netlifyRedirects(origin, prefix);
    case 'nginx':
      return nginxLocations(origin, articleHost, prefix);
    case 'framer':
      return FRAMER_COPY;
  }
}

/** The small caption above the code block (file or place the rule goes). `null` for Framer: it has no code block. */
export function proxySnippetCaption(provider: ProxyProvider, { publicBaseUrl }: ProxySnippetInput): string | null {
  const { host } = new URL(publicBaseUrl);
  const prefix = prefixOf(publicBaseUrl);
  switch (provider) {
    case 'cloudflare':
      return `Worker on the route ${host}${prefix ? `${prefix}*` : '/*'}`;
    case 'vercel':
      return 'vercel.json';
    case 'netlify':
      return '_redirects';
    case 'nginx':
      return `server { … } block of ${host}`;
    case 'framer':
      return null;
  }
}

export function robotsSitemapLine(publicBaseUrl: string): string {
  return `Sitemap: ${publicBaseUrl}/sitemap.xml`;
}
